import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { GenderProvider } from '@/context/GenderContext';
import { SiteConfigProvider } from '@/context/SiteConfigContext';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import Landing from '@/pages/Landing';
import Dashboard from '@/pages/Dashboard';
import Onboarding from '@/pages/Onboarding';
import Ebooks from '@/pages/Ebooks';
import Videos from '@/pages/Videos';
import VideosOrganized from '@/pages/VideosOrganized';
import DietCalculator from '@/pages/DietCalculator';
import MyDiet from '@/pages/MyDiet';
import MyHormonalCycle from '@/pages/MyHormonalCycle';
import MyWorkout from '@/pages/MyWorkout';
import Appointments from '@/pages/Appointments';
import Checkout from '@/pages/Checkout';
import PaymentSuccess from '@/pages/PaymentSuccess';
import AdminPanelNew from '@/pages/AdminPanelNew';
import MacroCalculator from '@/pages/MacroCalculator';
import ViewMealPlan from '@/pages/ViewMealPlan';
import TestLogin from '@/pages/TestLogin';
import DemoLogin from '@/pages/DemoLogin';
import WorkoutGenerator from '@/pages/WorkoutGenerator';
import DataProgresionNew from '@/pages/DataProgresionNew';
import NewWorkout from '@/pages/NewWorkout';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ResetPassword from '@/pages/ResetPassword';
import MyProgress from '@/pages/MyProgress';
import RenewalPage from '@/pages/RenewalPage';
import Recetarios from '@/pages/Recetarios';
import RecetarioFolder from '@/pages/RecetarioFolder';
import Tienda from '@/pages/Tienda';
import ProductDetail from '@/pages/ProductDetail';
import PersonalizedFormPage from '@/pages/PersonalizedFormPage';
import FitEvolutionSubscribe from '@/pages/FitEvolutionSubscribe';
import FitEvolutionDietRecommendation from '@/pages/FitEvolutionDietRecommendation';
import FitEvolutionUpdate from '@/pages/FitEvolutionUpdate';
import FitEvolutionRecipes from '@/pages/FitEvolutionRecipes';
import FitEvolutionPreparing from '@/pages/FitEvolutionPreparing';
import RecipeIdeas from '@/pages/RecipeIdeas';
import GoldCalendarPage from '@/pages/GoldCalendarPage';
import AdminAccess from '@/pages/AdminAccess';
import AdminWrapper from '@/pages/AdminWrapper';
import Plans from '@/pages/Plans';
import WorkoutHistory from '@/pages/WorkoutHistory';
import GlobalPaymentGate from '@/components/GlobalPaymentGate';
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const queryClient = new QueryClient();

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);

  // Service Worker desactivado - causaba problemas con actualizaciones
  // useEffect(() => {
  //   if ('serviceWorker' in navigator) {
  //     navigator.serviceWorker.addEventListener('message', (event) => {
  //       if (event.data && event.data.type === 'CACHE_UPDATED') {
  //         console.log('DeisyCFit: Nueva versión disponible:', event.data.version);
  //         window.location.reload();
  //       }
  //     });
  //     navigator.serviceWorker.ready.then(registration => {
  //       registration.update();
  //     });
  //   }
  // }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Primero verificar localStorage
      const loggedIn = localStorage.getItem('deisycfit_logged_in');
      const savedUser = localStorage.getItem('deisycfit_user');
      
      if (loggedIn === 'true' && savedUser) {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        setIsAuthenticated(true);
        return;
      }
      
      // Si no hay datos en localStorage, no está autenticado
      setIsAuthenticated(false);
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  // Special route for test login - bypass auth check
  if (window.location.pathname === '/test-login' || window.location.pathname === '/demo-login') {
    return (
      <QueryClientProvider client={queryClient}>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/test-login" element={<TestLogin />} />
              <Route path="/demo-login" element={<DemoLogin />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
      </QueryClientProvider>
    );
  }

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Cargando...</div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SiteConfigProvider>
        <GenderProvider user={user}>
          <div className="App">
            <BrowserRouter>
              <GlobalPaymentGate isAuthenticated={isAuthenticated} user={user} />
              <Routes>
                <Route path="/" element={<Landing isAuthenticated={isAuthenticated} user={user} />} />
                <Route path="/dashboard" element={isAuthenticated ? <Dashboard user={user} /> : <Navigate to="/" />} />
                <Route path="/onboarding" element={isAuthenticated ? <Onboarding user={user} /> : <Navigate to="/" />} />
              <Route path="/ebooks" element={<Ebooks isAuthenticated={isAuthenticated} user={user} />} />
              <Route path="/recetarios" element={<Recetarios isAuthenticated={isAuthenticated} user={user} />} />
              <Route path="/recetarios/folder/:folderId" element={<RecetarioFolder isAuthenticated={isAuthenticated} user={user} />} />
              <Route path="/tienda" element={<Tienda isAuthenticated={isAuthenticated} user={user} />} />
              <Route path="/tienda/producto/:id" element={<ProductDetail user={user} />} />
              <Route path="/plans" element={<Plans isAuthenticated={isAuthenticated} />} />
              <Route path="/videos" element={<Videos isAuthenticated={isAuthenticated} user={user} />} />
              <Route path="/videos-organized" element={<VideosOrganized user={user} />} />
              <Route path="/checkout/:planId" element={<Checkout isAuthenticated={isAuthenticated} user={user} />} />
              <Route path="/checkout" element={<Checkout isAuthenticated={isAuthenticated} user={user} />} />
              <Route path="/subscribe/fit-evolution" element={<FitEvolutionSubscribe />} />
              <Route path="/fit-evolution/diet-recommendation" element={<FitEvolutionDietRecommendation user={user} />} />
              <Route path="/fit-evolution/update" element={<FitEvolutionUpdate user={user} />} />
              <Route path="/fit-evolution/recipes" element={<FitEvolutionRecipes user={user} />} />
              <Route path="/fit-evolution/preparing" element={<FitEvolutionPreparing user={user} />} />
              <Route path="/diet-calculator" element={isAuthenticated ? <DietCalculator user={user} /> : <Navigate to="/" />} />
              <Route path="/my-diet" element={isAuthenticated ? <MyDiet user={user} /> : <Navigate to="/" />} />
              <Route path="/mi-ciclo-hormonal" element={isAuthenticated ? <MyHormonalCycle user={user} /> : <Navigate to="/" />} />
              <Route path="/my-workout" element={isAuthenticated ? <MyWorkout user={user} /> : <Navigate to="/" />} />
              <Route path="/my-progress" element={isAuthenticated ? <MyProgress user={user} /> : <Navigate to="/" />} />
              <Route path="/gold-calendar" element={isAuthenticated ? <GoldCalendarPage user={user} /> : <Navigate to="/" />} />
              <Route path="/renewal" element={isAuthenticated ? <RenewalPage user={user} /> : <Navigate to="/" />} />
              <Route path="/appointments" element={isAuthenticated ? <Appointments user={user} /> : <Navigate to="/" />} />
              {/* RUTAS DE ADMIN - Login unificado en /login */}
              <Route path="/admin-access" element={<Navigate to="/login" replace />} />
              <Route path="/admin" element={<AdminWrapper />} />
              <Route path="/admin-new" element={<AdminWrapper />} />
              <Route path="/admin-dashboard" element={<AdminWrapper />} />
              <Route path="/macro-calculator" element={isAuthenticated ? <MacroCalculator user={user} /> : <Navigate to="/" />} />
              <Route path="/meal-plan/:planId" element={isAuthenticated ? <ViewMealPlan user={user} /> : <Navigate to="/" />} />
              <Route path="/workout-generator" element={isAuthenticated ? <WorkoutGenerator user={user} /> : <Navigate to="/" />} />
              <Route path="/data-progresion" element={isAuthenticated ? <DataProgresionNew user={user} /> : <Navigate to="/" />} />
              <Route path="/workout-history" element={isAuthenticated ? <WorkoutHistory user={user} /> : <Navigate to="/" />} />
              <Route path="/new-workout" element={isAuthenticated ? <NewWorkout user={user} /> : <Navigate to="/" />} />
              <Route path="/personalized-form" element={isAuthenticated ? <PersonalizedFormPage user={user} /> : <Navigate to="/" />} />
              <Route path="/recipe-ideas" element={isAuthenticated ? <RecipeIdeas user={user} /> : <Navigate to="/" />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/payment-cancel" element={<PaymentSuccess />} />
              {/* URLs fijas de producción para MercadoPago - NO MODIFICAR */}
              <Route path="/pago/exito" element={<PaymentSuccess />} />
              <Route path="/pago/error" element={<PaymentSuccess />} />
              <Route path="/pago/pendiente" element={<PaymentSuccess />} />
              {/* Catch-all: cualquier ruta no reconocida vuelve al dashboard
                  para evitar pantalla en negro por enlaces rotos o URLs viejas. */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" />
          {/* PWAInstallPrompt desactivado - causaba problemas con actualizaciones */}
          {/* <PWAInstallPrompt /> */}
        </div>
      </GenderProvider>
      </SiteConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
